import { FC, useState } from "react";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FormType, ListEntry } from "../../common";
import { MasterDataOption, Maybe } from "../../../../../../graphql/types";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { BaselineFeedWithOrigin } from "../../../../models/Baseline";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import SustellSingleIngredientFeed from "../../../../../modules/Farms/Baseline/SustellSingleIngredientFeeds_v2";
import { Origin } from "../types";
import { CattleFeed as CattleFeedBaseline } from "../../../../models/Baseline/CattleBaseline";
import { CattleFeed } from "../../../../models/Intervention/CattleIntervention";
import { BeefIntervention } from "../../../../models/Intervention/BeefIntervention";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { formatLabelName } from "../../../../utils/string-utils";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../utils/unit-utils";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import ReactHookDsmSelect from "../../../../../modules/Helpers/ReactHookDsmSelect2";
import SustellInterventionSingleIngredientFeedComponent from "../../../../../modules/Farms/Intervention/SustellInterventionSingleIngredientFeedComponent";
import RowSelectWithMetricsV2 from "../../../../../modules/Farms/Intervention/RowSelectWithMetricsV2";

interface IBeefFeedSingleIngredientProps {
  singleIngredients: Maybe<Array<MasterDataOption>>;
  origins: Maybe<Array<Origin>>;
  formType: FormType;
  stageIndex: number;
  fieldItemPrefix: string;
  baselineStageData: CattleFeedBaseline;
  currentFormValues: CattleFeed | undefined | null;
  isSingleIngredientEdit: boolean;
  setIsSingleIngEdit: (val: boolean) => void;
}

const BeefFeedSingleIngredient: FC<IBeefFeedSingleIngredientProps> = ({
  singleIngredients,
  origins,
  formType = FormType.Add,
  stageIndex,
  fieldItemPrefix,
  baselineStageData,
  currentFormValues,
  isSingleIngredientEdit,
  setIsSingleIngEdit,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const fc = useFormContext<BeefIntervention>();

  const [, forceUpdate] = useState({});

  const userProfile = UserProfilePrefs.getInstance();
  const userUOM = userProfile.getUserUnitPrefs();
  const originalBarnOutputMassUnit = unitLong(userUOM.unitBarnOutputMass as string);

  let barnOutputMassUnit: string;
  switch (originalBarnOutputMassUnit) {
    case "kg":
      barnOutputMassUnit = "kg DM";
      break;
    case "lbs":
      barnOutputMassUnit = "lbs DM";
      break;
    default:
      originalBarnOutputMassUnit || "kg DM";
      break;
  }

  const singleIngOrigins: ListEntry[] = origins as ListEntry[];

  const {
    fields: ingredientsAdditions,
    append: appendIngredientsAdditions,
    remove: removeIngredientsAdditions,
  } = useFieldArray({
    name: `${fieldItemPrefix}.singleIngredientsAdditions`,
    control: fc.control,
    keyName: "keyId",
  });

  const shouldShowAdditions =
    isSingleIngredientEdit || ingredientsAdditions?.length !== 0;

  const cancelEditionHandler = () => {
    (baselineStageData?.singleIngredients || [])
      ?.filter((item) => !!item.feedType)
      ?.forEach((_, index) => {
        const lastValue = currentFormValues?.singleIngredients?.[index];
        fc.setValue(
          `${fieldItemPrefix}.singleIngredients[${index}].kgPerAnimal`,
          lastValue?.kgPerAnimal ?? ""
        );
        fc.setValue(
          `${fieldItemPrefix}.singleIngredients[${index}].origin`,
          lastValue?.origin ?? ""
        );
      });

    for (let i = ingredientsAdditions.length - 1; i >= 0; i--) {
      removeIngredientsAdditions(i);
    }

    const lastAdditions =
      (currentFormValues?.singleIngredientsAdditions as BaselineFeedWithOrigin[]) ??
      [];

    lastAdditions.forEach((addition) => {
      appendIngredientsAdditions(addition);
    });

    setIsSingleIngEdit(false);
    forceUpdate({});
  };

  const renderSingleIngredientUnit = (item: BaselineFeedWithOrigin, i: number) => {
    return (
      <>
        {!isSingleIngredientEdit ? (
          <DsmGrid className={classes.dsmGridTwoColumnsIngredientInterventionComponent}>
            {isFieldFilled(fc.getValues(`${fieldItemPrefix}.singleIngredients[${i}].kgPerAnimal`)) ? (
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.singleIngredients[${i}].kgPerAnimal`}
                tooltip={intl.formatMessage({ id: "INTERVENTION.FORM.BARN.RATION.SINGLE_INGREDIENTS.TOOLTIP" })}
                label={formatLabelName(item.feedType)}
                metricUnit={barnOutputMassUnit}
                baseline={item.kgPerAnimal}
                displayTypeSelect={false}
                type="number"
                disabled
              />
            ) : (
              <ReactHookDsmInput
                name={`${fieldItemPrefix}.singleIngredients[${i}].originalKgPerAnimal`}
                tooltip={intl.formatMessage({ id: "INTERVENTION.FORM.BARN.RATION.SINGLE_INGREDIENTS.TOOLTIP" })}
                label={formatLabelName(item.feedType)}
                defaultValue={item.kgPerAnimal ?? ''}
                adornment={barnOutputMassUnit}
                type="number"
                disabled
              />
            )}
            {isFieldFilled(fc.getValues(`${fieldItemPrefix}.singleIngredients[${i}].origin`)) ? (
              <RowSelectWithMetricsV2
                name={`${fieldItemPrefix}.singleIngredients[${i}].origin`}
                label={intl.formatMessage({ id: "SUSTELL.STAGE.FEED.SINGLE_INGREDIENT.ORIGIN" })}
                baseline={item.origin}
                options={singleIngOrigins}
                originalLabel={!!(
                  fc.getValues(`${fieldItemPrefix}.singleIngredients[${i}].origin`) &&
                  fc.getValues(`${fieldItemPrefix}.singleIngredients[${i}].origin`) !==
                  item.origin
                )}
                useStyle={false}
                disabled
              />
            ) : (
              <ReactHookDsmSelect
                name={`${fieldItemPrefix}.singleIngredients[${i}].originalOrigin`}
                label={intl.formatMessage({ id: "SUSTELL.STAGE.FEED.SINGLE_INGREDIENT.ORIGIN" })}
                defaultValue={item.origin}
                options={singleIngOrigins}
                adornment=""
                disabled
              />
            )}
          </DsmGrid>
        ) : (
          <SustellInterventionSingleIngredientFeedComponent
            formType={!isSingleIngredientEdit ? FormType.View : formType}
            singleIngredient={item}
            ingredientIndex={i}
            fieldItemPrefix={fieldItemPrefix}
            availableOrigins={singleIngOrigins}
            displayTypeSelect={isSingleIngredientEdit}
          />
        )}
      </>
    )
  }

  return (
    <div>
      <p className={classes.headingTitleVariant}>
        {intl.formatMessage({ id: "SUSTELL.STAGE.FEED.SINGLE_INGREDIENT" })}
      </p>

      <DsmGrid
        className={classes.dsmGridOneColumnInterventionWithBorder}
        style={{ padding: "var(--dsm-spacing-px-4)" }}
      >
        {(baselineStageData?.singleIngredients || [])
          ?.filter((item) => !!item.feedType && !!item.origin)
          ?.map((singleIngredientItem: BaselineFeedWithOrigin, i: number) =>
            (renderSingleIngredientUnit(singleIngredientItem, i))
          )}
        {shouldShowAdditions && (
          <SustellSingleIngredientFeed
            key="singleIngredients"
            label={intl.formatMessage({
              id: "SUSTELL.STAGE.FEED.SINGLE_INGREDIENT",
            })}
            barnItemIndex={stageIndex}
            availableFeedItems={singleIngredients}
            availableOrigins={origins}
            fieldItemPrefix={fieldItemPrefix}
            subFieldName="singleIngredientsAdditions"
            tooltip={
              <div key="feeds-form-dialog-singleIngredients">
                {intl.formatMessage(
                  {
                    id: "BASELINE.FORM.BARN.RATION.SINGLE_INGREDIENTS.TOOLTIP",
                  },
                  { br: <br /> }
                )}
              </div>
            }
            formState={isSingleIngredientEdit ? formType : FormType.View}
            feedData={ingredientsAdditions}
            fieldsAppenRemoveObject={{
              fields: ingredientsAdditions,
              append: appendIngredientsAdditions,
              remove: removeIngredientsAdditions,
            }}
            allowNewIngredients={isSingleIngredientEdit}
          />
        )}
        {formType !== FormType.View &&
          <EditModeButtons
            isEditing={isSingleIngredientEdit}
            onEdit={() => setIsSingleIngEdit(true)}
            onCancel={cancelEditionHandler}
          />}
      </DsmGrid>
    </div>
  );
};

export default BeefFeedSingleIngredient;
